import { MarketActivityIndicator } from '@market/react';
import {
  forwardRef, useImperativeHandle, useRef, useState, useEffect,
} from 'react';
import { CashLocalPreviewNux } from '../cash-local-preview-nux/cash-local-preview-nux';
import styles from './cash-local-preview.module.css';

export interface CashLocalPreviewProps {

  /** The URL to display in the preview iframe */
  readonly previewUrl?: string;

  /** Whether the preview is ready to be shown */
  readonly isReady?: boolean;

  /** Fade duration for loading transitions in milliseconds */
  readonly fadeDuration?: number;

  /**
   * Whether to enable prefetching of the iframe URL.
   * This can be used to ensure that the url resolves before attempting to load the frame.
   */
  readonly enablePrefetch?: boolean;

  /** Whether the NUX should be displayed */
  readonly shouldDisplayNux?: boolean;

  /** Function to handle navigation to complete profile */
  readonly onCompleteProfile?: () => void;

}

export interface CashLocalPreviewRef {

  /** Imperatively reload the preview iframe */
  reloadPreview: () => void;
}

// Prefetch function to check if URL returns 200
const prefetchUrl = async (url: string): Promise<boolean> => {
  try {
    const response = await fetch(url, { method: 'HEAD' });
    return response.ok;
  } catch {
    return false;
  }
};

const DEFAULT_FADE_DURATION = 200;
const PREFETCH_MAX_ATTEMPTS = 3;
const PREFETCH_RETRY_DELAY = 1000; // ms delay between attempts

export const CashLocalPreview = forwardRef<CashLocalPreviewRef, CashLocalPreviewProps>((props, ref) => {
  const {
    previewUrl, isReady = false, fadeDuration = DEFAULT_FADE_DURATION, enablePrefetch = false,
    shouldDisplayNux = false, onCompleteProfile = () => {},
  } = props;

  const [isPreviewLoading, setIsPreviewLoading] = useState(true);
  const [isFadingOut, setIsFadingOut] = useState(false);
  const [prefetchSuccessful, setPrefetchSuccessful] = useState(false);
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const prefetchAttempts = useRef(0);

  // Effect to handle prefetching
  useEffect(() => {
    if (!enablePrefetch || !previewUrl || prefetchSuccessful) {
      return;
    }

    const attemptPrefetch = async () => {
      prefetchAttempts.current = 0;

      while (prefetchAttempts.current < PREFETCH_MAX_ATTEMPTS) {
        prefetchAttempts.current += 1;
        const success = await prefetchUrl(previewUrl);
        if (success) {
          setPrefetchSuccessful(true);
          return;
        }
        // Wait before next attempt (except for the last attempt)
        if (prefetchAttempts.current < PREFETCH_MAX_ATTEMPTS) {
          await new Promise((resolve) => setTimeout(resolve, PREFETCH_RETRY_DELAY));
        }
      }
      // All attempts failed
    };

    attemptPrefetch();
  }, [enablePrefetch, previewUrl, prefetchSuccessful]);

  // Reset prefetch state when URL changes
  useEffect(() => {
    setPrefetchSuccessful(false);
    prefetchAttempts.current = 0;
  }, [previewUrl]);

  const handlePreviewLoad = () => {
    setIsFadingOut(true);
    setTimeout(() => {
      setIsPreviewLoading(false);
      setIsFadingOut(false);
    }, fadeDuration);
  };

  const reloadPreview = () => {
    setIsPreviewLoading(true);
    setIsFadingOut(false); // Reset fading state when reloading
    if (iframeRef.current) {
      iframeRef.current.src += '';
    }
  };

  // Expose the reloadPreview method to parent components
  useImperativeHandle(ref, () => ({
    reloadPreview,
  }));

  return (
    <>
      <CashLocalPreviewNux
        shouldDisplay={shouldDisplayNux}
        onCompleteProfile={onCompleteProfile}
      />
      <div className={styles.previewFrameContainer}>
        {isReady && previewUrl && (!enablePrefetch || prefetchSuccessful)
          ? (
            <iframe
              ref={iframeRef}
              className={styles.preview}
              title={'Cash Local Preview'}
              src={previewUrl}
              data-testid="cash-local-preview-frame"
              onLoad={handlePreviewLoad}
            />
          )
          : null
        }

        {/* Loading overlay - show during loading or prefetching */}
        {isPreviewLoading
          ? (
            <div
              data-testid="cash-local-preview-overlay"
              className={[
                styles.previewOverlay,
                styles.fadeOut,
                isFadingOut ? styles.fadeOutActive : '',
              ].join(' ')}
            >
              <MarketActivityIndicator
                data-testid="cash-local-preview-spinner"
                className={styles.previewSpinner}
              />
            </div>
          )
          : null}
      </div>
    </>
  );
});

CashLocalPreview.displayName = 'CashLocalPreview';
